<script lang="ts" setup>
/**
 * KeyValueDetails
 *
 * A fully generic display component. It receives a list of sections and renders
 * each one as a titled block containing a series of rows. Rows come in two
 * flavors:
 *
 *  - Plain rows   – just a value string rendered full-width (e.g. a name or
 *                   an address line).
 *  - Labeled rows – a label (always bold) and value; use valueWeight
 *                   'bold' (default) or 'normal' for the value.
 *  - Stacked rows  – a label on top with its value(s) below, optionally
 *                   followed by an inline action link (see RowAction).
 *
 * Sections whose `rows` array is empty, or where every value is falsy, are
 * hidden automatically so the parent never needs to filter them out first.
 *
 * Usage:
 *
 *   <key-value-details :sections="mySections" />
 *
 * Where `mySections` is of type `KeyValueSection[]`.
 */

export interface PlainRow {
    type: 'plain';
    value: string | null | undefined;
    /** Value font-weight: 'bold' (default) or 'normal'. Label is always bold. */
    valueWeight?: 'bold' | 'normal';
    /** When true, render the value with a `data-sensitive` attribute so Mixpanel
     * Session Replay masks it. Defaults to false (visible). */
    sensitive?: boolean;
}

export interface LabeledRow {
    type: 'labeled';
    label: string;
    value: string | null | undefined;
    /** Value font-weight: 'bold' (default) or 'normal'. Label is always bold. */
    valueWeight?: 'bold' | 'normal';
    /** When true, render the value with a `data-sensitive` attribute so Mixpanel
     * Session Replay masks it. Defaults to false (visible). */
    sensitive?: boolean;
}

/**
 * An inline text-and-icon action rendered at the bottom of a stacked row — e.g.
 * the "Edit address" link under the account holder on the confirm review screen.
 */
export interface RowAction {
    label: string;
    /** UILib glyph name, e.g. 'cd-ic-edit'. Omit for a label-only link. */
    icon?: string;
    dataId?: string;
    onClick: () => void;
}

export interface StackedRow {
    type: 'stacked';
    label?: string | null;
    values: (string | null | undefined)[];
    /** Value font-weight: 'bold' (default) or 'normal'. Label is always bold. */
    valueWeight?: 'bold' | 'normal';
    /** When true, render the value(s) with a `data-sensitive` attribute so Mixpanel
     * Session Replay masks them. Defaults to false (visible). */
    sensitive?: boolean;
    /** Optional action link rendered below the value(s). An action alone does not
     * make an otherwise-empty section visible. */
    action?: RowAction;
}

export type KeyValueRow = PlainRow | LabeledRow | StackedRow;

export interface KeyValueSection {
    /** Heading shown in accent colour above the rows. */
    title?: string;
    /** Ordered list of rows to render inside this section. */
    rows: KeyValueRow[];
}

interface Props {
    sections: KeyValueSection[];
}

const props = defineProps<Props>();

/**
 * A section is visible when it has at least one row whose value is non-empty.
 */
const isSectionVisible = (section: KeyValueSection): boolean => {
    const visible = section.rows.some((row) => {
        return row.type === 'stacked'
            ? row.values.some(Boolean)
            : !!row.value;
    });
    return visible;
};
   

const visibleSections = (): KeyValueSection[] =>
    props.sections.filter(isSectionVisible);

/**
 * Section visibility is decided per section, so a row can still be empty inside a
 * section kept visible by a sibling row. A stacked row with nothing to show
 * renders neither its label nor its values — but it keeps its action, which is
 * often the only way to supply the missing data.
 */
const hasValues = (row: StackedRow): boolean => row.values.some(Boolean);

const getValueWeightClass = (valueWeight: 'bold' | 'normal' | undefined): string => {
    return valueWeight === 'normal' ? 'key-value-details__value key-value-details__value--normal' : 'key-value-details__value';
};
</script>

<template>
    <div class="key-value-details">
        <div
            v-for="(section, sectionIndex) in visibleSections()"
            :key="sectionIndex"
            class="key-value-details__section"
        >
            <!-- Section heading -->
            <div v-if="section.title" class="key-value-details__section-title">
                {{ section.title }}
            </div>

            <template v-for="(row, rowIndex) in section.rows" :key="rowIndex">
                <!-- Plain row: full-width value -->
                <div
                    v-if="row.type === 'plain' && row.value"
                    :class="getValueWeightClass(row.valueWeight)"
                    :data-sensitive="row.sensitive ? '' : undefined"
                >
                    {{ row.value }}
                </div>

                <!-- Labeled row: label + value side by side -->
                <div
                    v-else-if="row.type === 'labeled' && row.value"
                    class="key-value-details__labeled-row"
                >
                    <h3 class="key-value-details__label">
                        {{ row.label }}
                    </h3>
                    <span
                        :class="getValueWeightClass(row.valueWeight)"
                        :data-sensitive="row.sensitive ? '' : undefined"
                    >
                        {{ row.value }}
                    </span>
                </div>

                <!-- Stacked row: label on top, value(s) below -->
                <div
                    v-else-if="row.type === 'stacked'"
                    class="key-value-details__stacked-row"
                >
                    <span
                        v-if="row.label && hasValues(row)"
                        class="key-value-details__label"
                    >
                        {{ row.label }}
                    </span>
                    <div v-if="hasValues(row)" class="key-value-details__stacked-values">
                        <span
                            v-for="(val, valIndex) in row.values.filter(Boolean)"
                            :key="valIndex"
                            :class="getValueWeightClass(row.valueWeight)"
                            :data-sensitive="row.sensitive ? '' : undefined"
                        >
                            {{ val }}
                        </span>
                    </div>

                    <app-button
                        v-if="row.action"
                        class="key-value-details__action"
                        type="transparent"
                        :icon="row.action.icon"
                        icon-position="left"
                        :data-id="row.action.dataId"
                        @onClick="row.action.onClick"
                    >
                        {{ row.action.label }}
                    </app-button>
                </div>
            </template>
        </div>

        <!-- Optional extra content rendered inside the gray area -->
        <slot />
    </div>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.key-value-details {
    display: flex;
    flex-direction: column;
    align-self: stretch;
    gap: 1.5rem;
    padding: 1rem;
    background: $gray-lightest;
    border-radius: 0 0 0.375rem 0.375rem;

    &__section {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    &__section-title {
        font-size: 1rem;
        font-weight: 700;
        letter-spacing: 1px;
        color: $theme-primary;
        text-transform: uppercase;
        padding-top: 0.4375rem;
    }

    &__labeled-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 1rem;
    }

    &__stacked-row {
        display: flex;
        flex-direction: column;
        gap: 0.375rem;
    }

    &__stacked-values {
        display: flex;
        flex-direction: column;
    }

    &__label {
        font-size: 1rem;
        font-weight: 700;
        color: $black-med;
        line-height: 150%;
        flex-shrink: 0;
        margin: 0;
    }

    &__action {
        align-self: flex-start;
        cursor: pointer;

        &.app-button {
            height: auto;
            padding: 0;
            justify-content: flex-start;
            font-size: 1rem;
            line-height: 150%;
        }

        &.app-button.transparent {
            color: $blue;
        }

        :deep(.app-button__icon) {
            font-size: 1.5rem;
        }

        :deep(.app-button__icon--left) {
            margin-right: 0.5rem;
        }
    }

    &__value {
        font-size: 1rem;
        color: $black-med;
        line-height: 150%;
        margin: 0;
        font-weight: 700;
        &--normal {
            font-weight: 400;
        }
    }
}
</style>
